import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import UserSelect from '../../../../shared/UserSelect';
import Button from '../../../../shared/Button';
import Alert from '../../../../shared/Alert';
import Dropzone from '../../../../shared/dropzone/Dropzone';
import Autocomplete from '../../../../shared/Autocomplete';
import Input from '../../../../shared/Input';

import { updateCaseAdmin } from '../../../../../actions/casesActions';

import { sortAlphabetically } from '../../../../../util/utils';
import { userRoles, caseConstants } from '../../../../../constants';
import { fileTypes } from '../../../../../constants/cases';

const { procedureFlows } = caseConstants;

const procedureSideFlows = [
  procedureFlows.HTO,
  procedureFlows.HTO_LATERAL,
  procedureFlows.DFO_Medial,
  procedureFlows.DFO_Lateral,
  procedureFlows.CUSTOM,
  procedureFlows.CUSTOM_EXTENDED,
  procedureFlows.MATORTHO,
  procedureFlows.MATORTHO_KNEE,
  procedureFlows.MATORTHO_HIP,
  procedureFlows.DOUBLE_LEVEL_OSTEOTOMY,
  procedureFlows.VILEX,
];

const planningFileFlows = [
  procedureFlows.HTO,
  procedureFlows.HTO_LATERAL,
  procedureFlows.DFO_Medial,
  procedureFlows.DOUBLE_LEVEL_OSTEOTOMY,
  procedureFlows.ACL,
  procedureFlows.CMF_EXTENDED,
  procedureFlows.MATORTHO,
  procedureFlows.MATORTHO_KNEE,
  procedureFlows.MATORTHO_HIP,
  procedureFlows.VILEX,
  procedureFlows.VILEX_H2,
  procedureFlows.VILEX_H3,
];

const Step0StaticAdmin = ({
  uid,
  userRole,
  surgeons,
  hospitals,
  procedures,
  surgeonList,
  activeCase,
  updateCaseAdmin,
  caseDistributor,
}) => {
  const startDate = moment().add(28, 'days');

  const findSurgeonDistributor = useMemo(
    () => surgeonList.find((caseSurgeon) => caseSurgeon.distributor === activeCase?.distributorId),
    [surgeonList, activeCase?.distributorId]
  );

  const findAssignedSurgeon = useMemo(
    () => surgeons.find((caseSurgeon) => caseSurgeon.uid === findSurgeonDistributor?.userId),
    [surgeons, findSurgeonDistributor?.userId]
  );

  const findSurgeon = () => {
    const currentSurgeon = surgeonList.find((item) => activeCase.surgeonId === item.id);

    if (currentSurgeon) {
      return surgeons.find((item) => item.uid === currentSurgeon.userId);
    }

    return null;
  };

  const [surgeon, setSurgeon] = useState(findSurgeon() || findAssignedSurgeon || null);
  const [distributor] = useState(caseDistributor || null);
  const [hospital, setHospital] = useState(
    hospitals.find((item) => item.name === activeCase.hospital) || null
  );
  const [procedure, setProcedure] = useState(
    procedures.find((item) => activeCase.procedure === item.id) || null
  );
  const [filteredHospitals, setHospitals] = useState(hospitals);
  const [date, setDate] = useState(activeCase.date ? moment(activeCase.date) : startDate);
  const [dateTBC, setDateTBC] = useState(!activeCase.date);
  const [firstName, setFirstName] = useState(activeCase.patientInfo.firstName || '');
  const [lastName, setLastName] = useState(activeCase.patientInfo.lastName || '');
  const [gender, setGender] = useState(activeCase.patientInfo.gender || 'male');
  const [dateOfBirth, setDateOfBirth] = useState(
    activeCase.patientInfo.dateOfBirth
      ? moment(activeCase.patientInfo.dateOfBirth)
      : moment('Jan 1, 1990')
  );
  const [procedureSide, setProcedureSide] = useState(activeCase.procedureSide || 'left');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [planningFileRequired, setPlanningFileRequired] = useState(
    activeCase.planningFileRequired ? 'yes' : 'no'
  );
  const [consultationDate, setConsultationDate] = useState(
    activeCase.consultationDate ? moment(activeCase.consultationDate) : moment().add(7, 'days')
  );
  const [isPaid, setPaid] = useState(activeCase?.isPaid || false);
  const [reports, setReports] = useState([]);

  const canEditCaseDate = userRole === userRoles.GLOBAL_ADMIN.name;
  const selectedProcedureFlow = [procedureFlows.VILEX_H2, procedureFlows.VILEX_H3].includes(
    procedure?.flow
  )
    ? procedureFlows.VILEX
    : procedure?.flow || activeCase.procedureFlow || '';
  const showProcedureSide = procedureSideFlows.includes(selectedProcedureFlow);
  const showPlanningFile = planningFileFlows.includes(selectedProcedureFlow);
  const showPaymentStatus = selectedProcedureFlow === procedureFlows.ACL;

  const selectedSurgeonRecord = useMemo(
    () =>
      surgeonList.find(
        (item) => item.userId === surgeon?.uid && item.distributor === distributor?.id
      ) ||
      surgeonList.find((item) => item.id === activeCase.surgeonId) ||
      null,
    [surgeonList, surgeon?.uid, distributor?.id, activeCase.surgeonId]
  );

  const checkMissingFields = () => (
    !distributor ||
    !surgeon ||
    !hospital ||
    !procedure ||
    !firstName ||
    !lastName ||
    !gender ||
    !dateOfBirth ||
    (showProcedureSide && !procedureSide)
  );

  const onUpload = (files) => {
    setReports((currentReports) => [
      ...currentReports,
      ...files.filter(
        (file) => !currentReports.some((reportFile) => reportFile.name === file.name)
      ),
    ]);
  };

  const onRemove = (name) => {
    setReports((currentReports) => currentReports.filter((file) => file.name !== name));
  };

  const getFilteredHospitals = (user) => {
    const currentSurgeon = user
      ? surgeonList
          .filter((item) => item.userId === user.uid)
          .find((caseSurgeon) => caseSurgeon.distributor === distributor?.id)
      : null;

    if (!currentSurgeon) {
      return hospitals;
    }

    return hospitals.filter((item) => currentSurgeon.hospitals.includes(item.id));
  };

  const handleSurgeonChange = (user) => {
    const nextHospitals = getFilteredHospitals(user);

    setSurgeon(user);
    setHospitals(nextHospitals);
    setHospital(nextHospitals.length ? nextHospitals[0] : null);
  };

  const filteredProcedures = useMemo(
    () =>
      procedures.filter((item) =>
        distributor?.assignedProcedures?.find((assign) => assign?.id === item?.id)
      ),
    [procedures, distributor]
  );

  const handleSubmit = () => {
    if (checkMissingFields() || !selectedSurgeonRecord?.id) {
      setError(true);
      return;
    }

    const now = moment().format();
    const files = [...(activeCase.files || [])];
    const newFiles = [];
    const isPlanningFileRequired = showPlanningFile && planningFileRequired === 'yes';

    reports.forEach((file) => {
      files.unshift({
        id: `${activeCase.id}_${fileTypes.report}_${file.name}`,
        name: file.name,
        type: fileTypes.report,
        date: now,
        userId: uid,
      });
      newFiles.push(file);
    });

    const caseData = {
      surgeon: selectedSurgeonRecord.id,
      surgeonId: selectedSurgeonRecord.id,
      surgeonData: selectedSurgeonRecord,
      distributorId: distributor.id,
      hospital: hospital.name,
      hospitalUniq: { id: hospital.id, name: hospital.name },
      procedure: procedure.id,
      procedureName: procedure.name,
      procedureFlow: selectedProcedureFlow,
      date: date && !dateTBC ? date.format() : null,
      procedureSide: showProcedureSide ? procedureSide : activeCase.procedureSide || procedureSide,
      patientInfo: {
        firstName,
        lastName,
        gender,
        dateOfBirth: moment.isMoment(dateOfBirth) ? dateOfBirth.format('ll') : dateOfBirth,
      },
      patientLastName: lastName,
      isPaid,
      files,
      planningFileRequired: isPlanningFileRequired,
      consultationDate:
        isPlanningFileRequired && consultationDate ? consultationDate.format() : null,
    };

    if (date && !dateTBC) {
      caseData.postponed = false;
    }

    setLoading(true);
    updateCaseAdmin(
      activeCase.id,
      { ...activeCase, ...caseData },
      {
        hospital: activeCase.hospital,
        date: activeCase.date,
        surgeon: activeCase.surgeon,
        procedure: activeCase.procedure,
      },
      newFiles
    ).then(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    const surgeonsForDistributor = surgeonList.filter(
      (caseSurgeon) => caseSurgeon.distributor === distributor?.id
    );
    const matchingSurgeon = surgeonsForDistributor.find(
      (caseSurgeon) => caseSurgeon.userId === surgeon?.uid
    );
    const user = surgeons.find((caseSurgeon) => caseSurgeon.uid === matchingSurgeon?.userId);
    const currentSurgeon = user
      ? surgeonList
          .filter((item) => item.userId === user.uid)
          .find((caseSurgeon) => caseSurgeon.distributor === distributor?.id)
      : null;

    if (!currentSurgeon) {
      setHospitals(hospitals);
      return;
    }

    setHospitals(hospitals.filter((item) => currentSurgeon.hospitals.includes(item.id)));
  }, [distributor, hospitals, surgeon, surgeonList, surgeons]);

  useEffect(() => {
    setProcedure(filteredProcedures.find((item) => activeCase.procedure === item.id) || null);
  }, [activeCase.procedure, filteredProcedures]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div className="case-step-container">
        <div className="case-step-content">
          <div className="case-step-row">
            <div className="modal-item-content">
              <div className="modal-item-block">
                <div className="block-header">Case info</div>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <div className="field-container">
                      <div className="field-title">Distributor</div>
                      <div className="field-content">
                        <div className="step0-surgeon-name">{distributor?.name || ''}</div>
                      </div>
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="field-container">
                      <div className="field-title">Assigned surgeon</div>
                      <div className="field-content">
                        {surgeon && activeCase.step > 3 ? (
                          <div className="step0-surgeon-name">
                            Dr. {surgeon.firstName} {surgeon.lastName}
                          </div>
                        ) : (
                          <UserSelect
                            onChange={handleSurgeonChange}
                            value={surgeon}
                            userList={surgeons}
                          />
                        )}
                      </div>
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="field-container">
                      <div className="field-title">Hospital</div>
                      <div className="field-content">
                        <Autocomplete
                          value={hospital}
                          onChange={setHospital}
                          options={filteredHospitals.sort(sortAlphabetically('name'))}
                          getOptionLabel={(option) => option?.name || ''}
                        />
                      </div>
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="field-container">
                      <div className="field-title">Procedure</div>
                      <div className="field-content">
                        {activeCase.step < 4 ? (
                          <Autocomplete
                            value={procedure}
                            onChange={setProcedure}
                            options={filteredProcedures.sort(sortAlphabetically('name'))}
                            getOptionLabel={(option) => option?.name || ''}
                          />
                        ) : (
                          <div className="step0-surgeon-name">{procedure?.name || ''}</div>
                        )}
                      </div>
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="field-container">
                      <div className="field-title">Targeted surgery date</div>
                      <div className="field-content">
                        <DatePicker
                          desktopModeMediaQuery="(min-width: 0px)"
                          format="DD/MM/YYYY"
                          value={date}
                          onChange={(newDate) => setDate(newDate)}
                          disabled={dateTBC || !canEditCaseDate}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: 'standard',
                            },
                          }}
                        />
                      </div>

                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={dateTBC}
                            disabled={!canEditCaseDate}
                            onChange={(e) => setDateTBC(e.target.checked)}
                          />
                        }
                        label="TBC (if date not yet confirmed)"
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>

              <div className="modal-item-block">
                <div className="block-header">Patient info</div>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="field-container">
                      <div className="field-title">Patient name (name, surname)</div>
                      <div className="field-content d-flex">
                        <div className="flex-1">
                          <Input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="First Name"
                          />
                        </div>

                        <div className="m-l-lg flex-1">
                          <Input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Last Name"
                          />
                        </div>
                      </div>
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="field-container">
                      <div className="field-title">Gender</div>
                      <div className="field-content">
                        <div className="radio-group-container">
                          <RadioGroup
                            aria-label="gender"
                            name="gender"
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                          >
                            <FormControlLabel
                              value="male"
                              control={<Radio color="primary" />}
                              label="Male"
                            />
                            <FormControlLabel
                              value="female"
                              control={<Radio color="primary" />}
                              label="Female"
                            />
                          </RadioGroup>
                        </div>
                      </div>
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="field-container">
                      <div className="field-title">Date of birth</div>
                      <div className="field-content">
                        <DatePicker
                          desktopModeMediaQuery="(min-width: 0px)"
                          format="DD/MM/YYYY"
                          value={dateOfBirth}
                          onChange={(newDate) => setDateOfBirth(newDate)}
                          disableFuture
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: 'standard',
                            },
                          }}
                        />
                      </div>
                    </div>
                  </Grid>

                  {showProcedureSide && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <div className="field-container">
                        <div className="field-title">Procedure side</div>
                        <div className="field-content">
                          <div className="radio-group-container">
                            <RadioGroup
                              aria-label="procedureSide"
                              name="procedureSide"
                              value={procedureSide}
                              onChange={(e) => setProcedureSide(e.target.value)}
                            >
                              <FormControlLabel
                                value="left"
                                control={<Radio color="primary" />}
                                label="Left"
                              />
                              <FormControlLabel
                                value="right"
                                control={<Radio color="primary" />}
                                label="Right"
                              />
                            </RadioGroup>
                          </div>
                        </div>
                      </div>
                    </Grid>
                  )}
                </Grid>
              </div>

              <div className="modal-item-block">
                <div className="block-header">Surgeon consultation report</div>
                <Dropzone
                  files={reports}
                  onUpload={onUpload}
                  icon="scan"
                  onRemove={onRemove}
                />
              </div>

              {showPlanningFile && (
                <div className="m-t-lg">
                  <div className="block-header">Planning File</div>
                  <div className="secondary">
                    Do you require a planning file to present to the patient prior to surgery?
                  </div>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <div className="radio-group-container">
                        <RadioGroup
                          aria-label="planningFileRequired"
                          name="planningFileRequired"
                          value={planningFileRequired}
                          onChange={(e) => setPlanningFileRequired(e.target.value)}
                        >
                          <FormControlLabel
                            value="no"
                            control={<Radio color="primary" />}
                            label="No"
                          />
                          <FormControlLabel
                            value="yes"
                            control={<Radio color="primary" />}
                            label="Yes"
                          />
                        </RadioGroup>
                      </div>
                    </Grid>

                    {planningFileRequired === 'yes' && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <div className="field-container">
                          <div className="field-title">Consultation date</div>
                          <div className="field-content">
                            <DatePicker
                              desktopModeMediaQuery="(min-width: 0px)"
                              format="DD/MM/YYYY"
                              value={consultationDate}
                              onChange={(newDate) => setConsultationDate(newDate)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  variant: 'standard',
                                },
                              }}
                            />
                          </div>
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </div>
              )}

              {showPaymentStatus && (
                <div className="modal-item-block">
                  <div className="block-header">Patient status</div>
                  <div className="field-container">
                    <div className="m-t-sm font-bold secondary">
                      Please check the box to confirm that payment has been received for this
                      case
                    </div>
                    <div className="field-content">
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={isPaid}
                            onChange={(e) => setPaid(e.target.checked)}
                          />
                        }
                        label="Payment Received"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="case-modal-actions">
          <div />
          <Button
            text="Save"
            loading={loading}
            disabled={loading}
            color="primary"
            onClick={handleSubmit}
          />
        </div>

        <Alert
          variant="warning"
          message="All fields are required"
          open={error}
          onClose={() => setError(false)}
        />
      </div>
    </LocalizationProvider>
  );
};

Step0StaticAdmin.propTypes = {
  uid: PropTypes.string,
  userRole: PropTypes.string,
  surgeons: PropTypes.array,
  hospitals: PropTypes.array,
  procedures: PropTypes.array,
  surgeonList: PropTypes.array,
  activeCase: PropTypes.object,
  updateCaseAdmin: PropTypes.func,
  caseDistributor: PropTypes.object,
};

const mapStateToProps = (state) => {
  const uid = state.user.currentUser.uid;
  const userRole = state.user.currentUser.role;
  const hospitals = state.hospitals.list;
  const surgeonList = state.surgeons.list?.filter((surgeon) => !surgeon.disabled) || [];
  const distributors = state.distributors.list;
  const activeCase = state.cases.activeCase;

  let surgeons = state.users.list.filter((user) => user.role === userRoles.SURGEON.name);

  const currentSurgeon = surgeonList.find((item) => item.id === uid);
  const surgeon = userRole === userRoles.SURGEON.name && surgeons.find((user) => user.uid === uid);

  const caseDistributor = distributors.find(
    (distributor) => distributor.id === activeCase?.distributorId
  );

  if (userRole === userRoles.SALES_REP.name) {
    const filteredSurgeons = surgeonList
      .filter((item) => item.salesRep.includes(uid))
      .map((item) => item.userId);

    surgeons = state.users.list.filter((user) => filteredSurgeons.includes(user.uid));
  }

  if (userRole === userRoles.PRACTICE_MANAGER.name) {
    const filteredSurgeons = surgeonList
      .filter((item) => item.manager.includes(uid))
      .map((item) => item.userId);

    surgeons = state.users.list.filter((user) => filteredSurgeons.includes(user.uid));
  }

  const hospitalList =
    surgeon && currentSurgeon
      ? hospitals.filter((item) =>
          surgeonList
            .filter((caseSurgeon) => caseSurgeon.userId === uid)
            .some((surgeonHospital) => surgeonHospital.hospitals.includes(item.id))
        )
      : hospitals;

  return {
    uid,
    userRole,
    surgeons,
    activeCase,
    caseDistributor,
    hospitals: hospitalList,
    procedures: state.procedures.list,
    surgeonList,
  };
};

const mapDispatchToProps = (dispatch) => ({
  updateCaseAdmin: (id, data, previousData, files) =>
    dispatch(updateCaseAdmin(id, data, previousData, files)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Step0StaticAdmin);
